import { MenuDrawer } from '@/scripts/theme/menu-drawer'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { ATTRIBUTES, SELECTORS } from '@/scripts/core/global'

export class HeaderDrawer extends MenuDrawer {
	static override htmlSelector = 'header-drawer'
	static override selectors = {
		...MenuDrawer.selectors,
	}
	header?: HTMLElement
	borderOffset?: number
	hoverCloseTimer?: number
	tileCarouselTimers: Map<HTMLElement, number> = new Map()
	constructor() {
		super()
		this.bindHoverEvents()
	}

	startTileCarousels() {
		this.querySelectorAll<HTMLElement>('[data-megadrawer-tile-carousel]').forEach((carousel) => {
			if (this.tileCarouselTimers.has(carousel)) return
			const track = carousel.querySelector<HTMLElement>('.megadrawer__tile-track')
			if (!track) return

			// First-time setup: clone the first page to the end so the wrap looks like another forward slide
			if (!track.dataset.realPages) {
				const realPageCount = track.children.length
				if (realPageCount <= 1) return
				const firstPage = track.firstElementChild
				if (firstPage) {
					const clone = firstPage.cloneNode(true) as HTMLElement
					clone.setAttribute('aria-hidden', 'true')
					// Strip ids from the clone so NavTile-* ids stay unique in the DOM
					clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'))
					track.appendChild(clone)
				}
				track.dataset.realPages = realPageCount.toString()
			}

			const realPageCount = parseInt(track.dataset.realPages || '0', 10)
			if (realPageCount <= 1) return
			const intervalMs = parseInt(carousel.dataset.autoscrollMs || '4000', 10)

			// Reset to start without animating back
			track.style.transition = 'none'
			track.style.transform = 'translateX(0)'
			void track.offsetHeight
			track.style.transition = ''

			let idx = 0
			let snapTimeout: number | undefined

			const timer = window.setInterval(() => {
				idx++
				track.style.transition = ''
				track.style.transform = `translateX(${-idx * 100}%)`

				// Replay the existing tile fade-in on the page now coming into view
				const visiblePage = track.children[idx] as HTMLElement | undefined
				visiblePage?.querySelectorAll<HTMLElement>('.megadrawer__tile-img').forEach((img) => {
					img.style.animation = 'none'
					void img.offsetHeight
					img.style.animation = ''
				})

				if (idx === realPageCount) {
					// Slid onto the clone (visually identical to page 0). After the transition completes,
					// snap to real page 0 instantly so the next forward tick is still forward.
					if (snapTimeout) window.clearTimeout(snapTimeout)
					snapTimeout = window.setTimeout(() => {
						track.style.transition = 'none'
						track.style.transform = 'translateX(0)'
						void track.offsetHeight
						track.style.transition = ''
						idx = 0
					}, 650)
				}
			}, intervalMs)

			this.tileCarouselTimers.set(carousel, timer)
		})
	}

	stopTileCarousels() {
		this.tileCarouselTimers.forEach((timer) => window.clearInterval(timer))
		this.tileCarouselTimers.clear()
		this.querySelectorAll<HTMLElement>('.megadrawer__tile-track').forEach((track) => {
			track.style.transition = 'none'
			track.style.transform = ''
		})
	}

	override getInstanceSelectors() {
		this.instanceSelectors = HeaderDrawer.selectors
	}

	override onMainSummaryClick(event: MouseEvent) {
		const isDesktop =
			this.dataset.breakpoint === 'desktop' &&
			window.matchMedia('(min-width: 990px)').matches
		const linkUrl = this.dataset.linkUrl
		if (isDesktop && linkUrl && linkUrl !== '' && linkUrl !== '#') {
			try {
				const target = new URL(linkUrl, window.location.origin)
				if (target.pathname !== window.location.pathname) {
					event.preventDefault()
					event.stopPropagation()
					window.location.href = linkUrl
					return
				}
			} catch {
				// invalid URL — fall through to default toggle
			}
		}
		super.onMainSummaryClick(event)
	}

	bindHoverEvents() {
		if (this.dataset.breakpoint !== 'desktop') return
		if (typeof window.matchMedia !== 'function') return
		if (!window.matchMedia('(hover: hover)').matches) return
		this.addEventListener('mouseenter', this.onHoverEnter)
		this.addEventListener('mouseleave', this.onHoverLeave)
		document.addEventListener('click', this.onDocumentClick)
		// Collapse an expanded megadrawer as soon as the cursor leaves the viewport entirely
		// (mouseleave on <html> fires when the pointer exits the window).
		document.documentElement.addEventListener('mouseleave', this.onViewportLeave)
	}

	onViewportLeave = () => {
		if (!window.matchMedia('(min-width: 990px)').matches) return
		if (!this.mainDetails.hasAttribute('open')) return
		if (this.hoverCloseTimer) {
			window.clearTimeout(this.hoverCloseTimer)
			this.hoverCloseTimer = undefined
		}
		this.closeMenuDrawer(undefined, undefined)
	}

	onDocumentClick = (event: MouseEvent) => {
		if (!window.matchMedia('(min-width: 990px)').matches) return
		if (!this.mainDetails.hasAttribute('open')) return
		const target = event.target as Node | null
		if (target && this.contains(target)) return
		this.closeMenuDrawer(undefined, this.mainSummary)
	}

	onHoverEnter = () => {
		if (!window.matchMedia('(min-width: 990px)').matches) return
		if (this.hoverCloseTimer) {
			window.clearTimeout(this.hoverCloseTimer)
			this.hoverCloseTimer = undefined
		}
		document.querySelectorAll<HeaderDrawer>('header-drawer[data-breakpoint="desktop"]').forEach((other) => {
			if (other === this) return
			const otherDetails = other.querySelector('details[open]') as HTMLDetailsElement | null
			if (otherDetails) {
				other.closeMenuDrawer(undefined, undefined)
			}
		})
		if (this.mainDetails.hasAttribute('open')) return
		this.mainDetails.setAttribute('open', '')
		this.openMenuDrawer(this.mainSummary, { focus: false })
	}

	onHoverLeave = () => {
		if (!window.matchMedia('(min-width: 990px)').matches) return
		if (this.hoverCloseTimer) window.clearTimeout(this.hoverCloseTimer)
		const delayMs = parseInt(this.dataset.hoverCloseMs || '3000', 10)
		this.hoverCloseTimer = window.setTimeout(() => {
			if (this.mainDetails.hasAttribute('open')) {
				this.closeMenuDrawer(undefined, undefined)
			}
		}, delayMs)
	}

	setHeader() {
		return this.header || q.rs(SELECTORS.sectionHeader)
	}

	setBorderOffset() {
		return this.borderOffset ||
			q.rs(SELECTORS.headerWrapper).dataset.ucHeaderWrapper == 'border-bottom'
			? 1
			: 0
	}

	override openMenuDrawer(summaryElement: HTMLElement, options: { focus?: boolean } = {}) {
		this.header = this.setHeader()
		this.borderOffset = this.setBorderOffset()
		document.documentElement.style.setProperty(
			'--header-bottom-position',
			`${Math.round(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
		)
		document.documentElement.classList.add('menu-drawer-open')
		this.header.setAttribute('data-uc-header-menu-open', '')

		setTimeout(() => {
			this.mainDetails.setAttribute(ATTRIBUTES.menuOpening, '')
		})

		summaryElement.setAttribute('aria-expanded', 'true')
		window.addEventListener('resize', this.onResize)
    window.TsDOM.trapFocus(this.mainDetails, summaryElement, options)
		document.body.classList.add(`overflow-hidden`)
		document.addEventListener('click', this.onDocClickOutside)
		this.startTileCarousels()
	}

	onDocClickOutside = (event: MouseEvent) => {
		if (!this.mainDetails.hasAttribute('open')) return
		const target = event.target as Node | null
		if (!target || this.contains(target)) return
		this.closeMenuDrawer(event, undefined)
	}

	override closeMenuDrawer(
		event: Event | undefined,
		elementToFocus: HTMLElement | undefined = undefined
	) {
		this.header = this.setHeader()
		super.closeMenuDrawer(event, elementToFocus)
		this.header.removeAttribute('data-uc-header-menu-open')
		window.removeEventListener('resize', this.onResize)
		document.removeEventListener('click', this.onDocClickOutside)
		document.body.classList.remove(`overflow-hidden`)
    document.documentElement.classList.remove('menu-drawer-open')
		this.stopTileCarousels()
	}

  override closeSubmenu(detailsElement: HTMLElement) {
    document.documentElement.classList.remove('submenu-drawer-open')
    document.documentElement.classList.remove('menu-drawer-open')

    const parentMenuElement = detailsElement.closest(this.instanceSelectors.submenuOpen)
    parentMenuElement && parentMenuElement.setAttribute(ATTRIBUTES.submenu, 'closed')
    detailsElement.removeAttribute(ATTRIBUTES.menuOpening)
    const summaryElement = q.rs('summary', detailsElement)
    summaryElement.setAttribute('aria-expanded', 'false')
    window.TsDOM.removeTrapFocus(summaryElement)
    this.closeAnimation(detailsElement, true)
  }

	onResize = () => {
		if (!this.header) return
		this.borderOffset = this.setBorderOffset()
		document.documentElement.style.setProperty(
			'--header-bottom-position',
			`${Math.round(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
		)
		document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`)
	}
}
